/**
 * External Dependencies
 */
import ClipboardJS from 'clipboard';

// Convert copy-link links to buttons to be used by clipboard feature
for (const link of document.body.querySelectorAll('.wp-social-link-chain a')) {
  const button = document.createElement('button');
  for (const child of link.children) {
    button.appendChild(child);
  }
  button.setAttribute('aria-label', link.getAttribute('aria-label'));
  button.setAttribute('data-clipboard-text', link.href);
  link.parentElement.replaceChild(button, link);
}

// Copy to clipboard feature
const clipboard = new ClipboardJS('.wp-social-link-chain button');
clipboard.on('success', (e) => {
  const announcement = document.createElement('p');
  const restoreOriginalLabel = () => {
    e.trigger.nextSibling.textContent = '';
    delete e.trigger.dataset.label;
    e.trigger.parentElement.removeChild(announcement);

    // On desktop we need to blur the button so that it hides the text
    if (document.activeElement === e.trigger) {
      e.trigger.blur();
    }
  }

  e.trigger.setAttribute('data-label', 'Copied link');
  // @see https://github.com/zenorocha/clipboard.js/issues/695
  e.trigger.focus();
  // Restore the original element once focus moves away
  e.trigger.parentElement.addEventListener('mouseleave', restoreOriginalLabel, {once: true});
  e.trigger.parentElement.addEventListener('focusout', restoreOriginalLabel, {once: true});

  // Add an announcement for screen readers when the link has been copied.
  announcement.classList.add('sr-only')
  announcement.setAttribute('role', 'region');
  announcement.setAttribute('aria-live', 'polite');
  announcement.textContent = 'Copied link';
  e.trigger.parentElement.insertBefore(announcement, e.trigger.nextSibling);
});

// Basic AJAX pagination
document.addEventListener('click', (e) => {
  const link = e.target;
  if (!link.matches('.pagination a')) {
    return;
  }
  if (link.host !== window.location.host) {
    return;
  }

  const view = link.closest('[id]');
  view.classList.add('is-loading');
  link.classList.add('is-disabled');

  window.fetch(link)
    .then((response) => response.text())
    .then((response) => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(response, 'text/html');
      const newView = dom.getElementById(view.id);

      // Update the status message for screen readers.
      const newPaginationCurrent = newView.querySelector('.pagination [aria-current="page"]').textContent;
      const newPaginationTotal = newView.querySelectorAll('.pagination li').length;
      newView.querySelector('[aria-live="polite"]').textContent = `Page ${newPaginationCurrent} out of ${newPaginationTotal} loaded.`;

      // Replace the previous content
      view.innerHTML = newView.innerHTML;
      view.classList.remove('is-loading');

      // Wait for the DOM to render and then scroll to the top of the list.
      requestAnimationFrame(() => view.scrollIntoView());
      // Move focus to the list
      view.querySelector('[aria-live="polite"]').nextElementSibling.focus();
    })
    .catch(() => {
      link.classList.remove('is-disabled');
    });

  e.preventDefault();
}, true);

// Make PDF links accessible by adding an icon with a label about it
// downloading the file. This also enforces the file to be downloaded.
for (const link of document.body.querySelectorAll('a[href$=".pdf"]')) {
  const icon = document.createElement('i');
  icon.classList.add('pdf-icon');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('title', 'Download PDF');

  const srLabel = document.createElement('span');
  srLabel.classList.add('sr-only');
  srLabel.textContent = '(download PDF)';

  link.appendChild(icon);
  link.appendChild(srLabel);
  link.classList.add('is-pdf-link');
  link.setAttribute('download', '');
  link.removeAttribute('target');
}

// Make external links accessible by adding an icon with a label about it
// opening in a new window.
for (const link of document.body.querySelectorAll('a[target="_blank"]:not(.wp-block-social-link-anchor)')) {
  const icon = document.createElement('i');
  icon.classList.add('external-link-icon');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('title', 'Open in new window');

  const srLabel = document.createElement('span');
  srLabel.classList.add('sr-only');
  srLabel.textContent = '(open in new window)';

  link.appendChild(icon);
  link.appendChild(srLabel);
  link.classList.add('is-external-link');
}

// Mark the wrapping dropdown label with an activate state class for styling
// purposes.
let hasFiltered = false;
window.FWP?.hooks?.addAction('facetwp/refresh/dropdown', ($this, facet_name) => {
  let value = window.FWP.facets[facet_name];
  value = value?.length > 0 ? value[0] : null;

  if (!value && !hasFiltered && window.location.search) {
    const urlParams = new URLSearchParams(window.location.search);
    value = urlParams.get(`_${facet_name}`);
  }

  const selectEl = $this.nodes[0];
  const labelEl = selectEl.closest('.facetwp-filter')

  if (value) {
    labelEl.classList.add('is-active');
  } else {
    labelEl.classList.remove('is-active');
  }

  hasFiltered = true;
});


let isPaginationClicked = false;
document.addEventListener('click', (e) => {
  isPaginationClicked = e.target?.matches('.facetwp-page[data-page]');
});

window.FWP?.hooks?.addAction('facetwp/loaded', () => {
  // Exit on first page load
  if (!window.FWP.loaded) {
    return false;
  }

  const view = document.querySelector('.facetwp-template');
  const srMessage = view.closest('#listing').querySelector('[aria-live="polite"]');

  const newPaginationCurrent = window.FWP.settings.pager.page;
  const newPaginationTotal = window.FWP.settings.pager.total_pages;

  srMessage.textContent = `Page ${newPaginationCurrent} out of ${newPaginationTotal} loaded.`;

  // Scroll to top if pagination is used
  if (isPaginationClicked) {
    srMessage.nextElementSibling.focus({preventScroll: true});
    srMessage.nextElementSibling.scrollIntoView();
  }
});

const openCookiebotLink = document.querySelector('.open-cookiebot a');

if (openCookiebotLink) {
  openCookiebotLink.addEventListener('click', (e) => {
    e.preventDefault();
    window.Cookiebot?.show();
  });
}

// Function to append styles to shadow root
function appendStylesToShadowRoot(shadowRoot) {
  shadowRoot.append(
    Object.assign(document.createElement("STYLE"), {
      innerText: `div.menu-item button span {
        background: #75cfeb;
        border-radius: 50%;
        margin-left: 5px;
      }`
    }),
    Object.assign(document.createElement("STYLE"), {
      innerText: `div.menu-item button span slot {
        font-weight: 800;
        font-size: 15px;
      }`
    })
  );
}

// Observe changes in the document to detect when the shadow DOM is available
const observer = new MutationObserver((mutationsList, observer) => {
  for (const mutation of mutationsList) {
    if (mutation.type === 'childList') {
      // Target shadow root in subnav
      const nestedMenuItem = document.querySelector("gds-menu-item-nested");
      if (nestedMenuItem && nestedMenuItem.shadowRoot) {
        appendStylesToShadowRoot(nestedMenuItem.shadowRoot);
        observer.disconnect(); // Stop observing once the shadow DOM is found and styles are appended
      }
    }
  }
});

// Start observing the document
observer.observe(document.body, { childList: true, subtree: true });

// Initial check in case the element is already present
const nestedMenuItem = document.querySelector("gds-menu-item-nested");
if (nestedMenuItem && nestedMenuItem.shadowRoot) {
  appendStylesToShadowRoot(nestedMenuItem.shadowRoot);
  observer.disconnect();
}